import {
  defaultTheme,
  parseThemePreferences,
  themeStorageKey,
  type ThemePreferences,
} from './theme-options'

export interface ThemeSnapshot extends ThemePreferences {
  resolvedTheme: 'light' | 'dark'
}
const serverSnapshot: ThemeSnapshot = { ...defaultTheme, resolvedTheme: 'dark' }
let snapshot = serverSnapshot
let initialized = false
let media: MediaQueryList | undefined
const listeners = new Set<() => void>()

function readStoredPreferences(): ThemePreferences {
  try {
    return parseThemePreferences(
      JSON.parse(window.localStorage.getItem(themeStorageKey) ?? 'null'),
    )
  } catch {
    return { ...defaultTheme }
  }
}

function apply(preferences: ThemePreferences) {
  const resolvedTheme =
    preferences.mode === 'system'
      ? media?.matches
        ? 'dark'
        : 'light'
      : preferences.mode
  if (typeof document !== 'undefined') {
    const root = document.documentElement
    root.dataset.theme = resolvedTheme
    root.dataset.palette = preferences.palette
    root.dataset.accent = preferences.accent
    root.style.colorScheme = resolvedTheme
    const browserColor = getComputedStyle(root)
      .getPropertyValue('--bg-base')
      .trim()
    if (browserColor)
      document
        .querySelector('meta[name=theme-color]')
        ?.setAttribute('content', browserColor)
  }
  if (
    snapshot.mode === preferences.mode &&
    snapshot.palette === preferences.palette &&
    snapshot.accent === preferences.accent &&
    snapshot.resolvedTheme === resolvedTheme
  )
    return
  snapshot = { ...preferences, resolvedTheme }
  listeners.forEach((listener) => listener())
}

export function initializeTheme() {
  if (initialized || typeof window === 'undefined') return
  initialized = true
  media = window.matchMedia?.('(prefers-color-scheme: dark)')
  apply(readStoredPreferences())
  media?.addEventListener('change', () => {
    if (snapshot.mode === 'system') apply(snapshot)
  })
  window.addEventListener('storage', (event) => {
    if (event.key !== themeStorageKey && event.key !== null) return
    try {
      apply(parseThemePreferences(JSON.parse(event.newValue ?? 'null')))
    } catch {
      apply(defaultTheme)
    }
  })
}

export function setThemePreferences(patch: Partial<ThemePreferences>) {
  initializeTheme()
  const next = parseThemePreferences({ ...snapshot, ...patch })
  try {
    window.localStorage.setItem(themeStorageKey, JSON.stringify(next))
  } catch {
    /* Preferences still work when storage is blocked. */
  }
  apply(next)
}
export function subscribeToTheme(listener: () => void) {
  initializeTheme()
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
export const getThemeSnapshot = () => snapshot
export const getServerThemeSnapshot = () => serverSnapshot
